if (!customElements.get('packaging-selector')) {
  customElements.define(
    'packaging-selector',
    class PackagingSelector extends HTMLElement {
      constructor() {
        super();

        this.cartDrawerElement = document.querySelector('cart-drawer');
        this.sectionId = this.getAttribute('data-section-id');

        this.quantityInputs = this.querySelectorAll('.packaging-selector__input');
        this.clearButtons = this.querySelectorAll('.packaging-selector__clear');
        this.addButtons = this.querySelectorAll('.packaging-selector__add');

        this.pendingNavigationUrl = null;
        this.allowNavigation = false;

        this.boundColorSwatchClick = this.handleColorSwatchClick.bind(this);
        this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
        this.boundQuickViewClose = this.handleQuickViewClose.bind(this);
        this.boundQuickViewEscape = this.handleQuickViewEscape.bind(this);

        this.pendingCloseModal = null;
        this.allowClose = false;

        this.bindEvents();
        this.bindUnsavedQuantitiesGuard();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundColorSwatchClick);
        document.removeEventListener('click', this.boundQuickViewClose, true);
        document.removeEventListener('keyup', this.boundQuickViewEscape, true);
        window.removeEventListener('beforeunload', this.boundBeforeUnload);
      }

      // The native product option picker (Size, Packaging, ...) is hidden via CSS since this
      // component replaces it with its own tables, but it's kept in the DOM and left enabled so
      // it can still be driven programmatically: other blocks on the page (price, media, SKU)
      // read their variant from its checked inputs, so we sync it to whichever row the customer
      // is currently working on.
      get variantSelects() {
        return document.getElementById(`variant-selects-${this.sectionId}`);
      }

      bindEvents() {
        this.quantityInputs.forEach((input) => {
          input.addEventListener('change', this.handleQuantityChange.bind(this));
          input.addEventListener('input', this.handleQuantityInput.bind(this));
          input.addEventListener('focus', this.handleQuantityFocus.bind(this));
        });

        this.clearButtons.forEach((button) => {
          button.addEventListener('click', this.handleClearAll.bind(this));
        });

        this.addButtons.forEach((button) => {
          button.addEventListener('click', this.handleAddToCart.bind(this));
        });
      }

      get unsavedAlert() {
        return document.getElementById(`PackagingUnsavedAlert-${this.sectionId}`);
      }

      bindUnsavedQuantitiesGuard() {
        document.addEventListener('click', this.boundColorSwatchClick);
        document.addEventListener('click', this.boundQuickViewClose, true);
        document.addEventListener('keyup', this.boundQuickViewEscape, true);
        window.addEventListener('beforeunload', this.boundBeforeUnload);

        const alert = this.unsavedAlert;
        if (!alert) return;

        alert.querySelector('.packaging-selector__alert-stay')?.addEventListener('click', () => {
          this.pendingCloseModal = null;
          this.pendingNavigationUrl = null;
          alert.hide();
        });
        alert
          .querySelector('.packaging-selector__alert-continue')
          ?.addEventListener('click', this.confirmPendingExit.bind(this));
      }

      get quickViewModal() {
        return this.closest('quick-view-modal');
      }

      handleQuickViewClose(event) {
        const modal = this.quickViewModal;
        if (!modal || this.allowClose || !modal.hasAttribute('open')) return;
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.target.closest('.packaging-selector__alert')) return;

        const trigger = event.target.closest('.drawer__close-btn, .fixed-overlay');
        if (!trigger || trigger.getAttribute('aria-controls') !== modal.id) return;
        if (!this.hasUnsavedQuantities()) return;

        event.preventDefault();
        event.stopPropagation();
        this.pendingCloseModal = modal;

        const alert = this.unsavedAlert;
        if (!alert) {
          this.confirmPendingExit();
          return;
        }

        alert.show(trigger);
      }

      handleQuickViewEscape(event) {
        if (event.code !== 'Escape') return;

        const modal = this.quickViewModal;
        if (!modal || this.allowClose || !modal.hasAttribute('open')) return;
        if (!this.hasUnsavedQuantities()) return;

        const alert = this.unsavedAlert;
        if (alert && alert.hasAttribute('open')) return;

        event.preventDefault();
        event.stopPropagation();
        this.pendingCloseModal = modal;

        if (!alert) {
          this.confirmPendingExit();
          return;
        }

        alert.show();
      }

      confirmPendingExit() {
        const modal = this.pendingCloseModal;
        if (!modal) {
          this.confirmNavigation();
          return;
        }

        this.pendingCloseModal = null;
        this.allowClose = true;
        this.unsavedAlert?.hide();
        modal.hide();
      }

      hasUnsavedQuantities() {
        return Array.from(this.quantityInputs).some((input) => (parseInt(input.value) || 0) > 0);
      }

      handleColorSwatchClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const swatch = event.target.closest('.product-color-swatches__item');
        if (!swatch || swatch.classList.contains('is-current') || !swatch.href) return;
        if (!this.hasUnsavedQuantities()) return;

        event.preventDefault();
        this.pendingNavigationUrl = swatch.href;

        const alert = this.unsavedAlert;
        if (!alert) {
          this.confirmNavigation();
          return;
        }

        alert.show(swatch);
      }

      confirmNavigation() {
        if (!this.pendingNavigationUrl) return;

        this.allowNavigation = true;
        window.location.href = this.pendingNavigationUrl;
      }

      handleBeforeUnload(event) {
        if (this.allowNavigation || !this.hasUnsavedQuantities()) return;

        event.preventDefault();
        event.returnValue = '';
      }

      getSection(packagingType) {
        return this.querySelector(`.packaging-selector__section[data-packaging-type="${packagingType}"]`);
      }

      // Quantity (rounded to the row's case-pack multiple, capped at the row's max) times unit price.
      getRowSubtotal(row) {
        const input = row.querySelector('.packaging-selector__input');
        const priceCell = row.querySelector('.packaging-selector__cell--price');
        const quantity = parseInt(input.value) || 0;
        const price = parseInt(priceCell.dataset.price) || 0;
        return quantity * price;
      }

      // Snap a typed quantity to something purchasable: clamp to [0, max], then round DOWN to
      // the nearest whole case pack. A buyer can't order a partial case or more than what's in
      // stock, so 31 units with a case pack of 12 (31 available) becomes 24 — never 36.
      normalizeQuantity(value, quantityJump, maxQuantity) {
        if (value <= 0) return 0;
        if (value > maxQuantity) value = maxQuantity;
        return Math.floor(value / quantityJump) * quantityJump;
      }

      // Runs on blur/change only — never while typing — so it doesn't fight the user mid-entry.
      handleQuantityChange(event) {
        const input = event.target;
        const row = input.closest('.packaging-selector__row');
        const quantityJump = parseInt(row.dataset.quantityJump) || 1;
        const maxQuantity = parseInt(row.dataset.maxQuantity);

        input.value = this.normalizeQuantity(parseInt(input.value) || 0, quantityJump, maxQuantity);
        input.setCustomValidity('');

        this.syncNativeVariantPicker(row);
        this.updateRowTotal(row);
        this.updateSectionTotal(input.dataset.packagingType);
      }

      // While typing, only surface a non-blocking hint if the value is over stock; the value
      // itself is left alone and normalized once on blur (handleQuantityChange).
      handleQuantityInput(event) {
        const input = event.target;
        const maxQuantity = parseInt(input.closest('.packaging-selector__row').dataset.maxQuantity);
        const value = parseInt(input.value) || 0;

        input.setCustomValidity(value > maxQuantity ? `Only ${maxQuantity} in stock` : '');
      }

      handleQuantityFocus(event) {
        const row = event.target.closest('.packaging-selector__row');
        if (row) this.syncNativeVariantPicker(row);
      }

      // Checks the native option inputs (Size, Packaging, ...) matching this row's variant and
      // dispatches a change event, so variant-selects' listeners (price, media, SKU, etc.) pick
      // up whichever variant the customer is currently working on in the table.
      syncNativeVariantPicker(row) {
        const variantSelects = this.variantSelects;
        const optionValueIds = row.dataset.optionValueIds;
        if (!variantSelects || !optionValueIds) return;

        let lastChangedInput = null;

        optionValueIds
          .split(',')
          .filter(Boolean)
          .forEach((optionValueId) => {
            const optionInput = variantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);
            if (!optionInput || optionInput.checked) return;
            optionInput.checked = true;
            lastChangedInput = optionInput;
          });

        if (lastChangedInput) {
          lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateRowTotal(row) {
        const totalCell = row.querySelector('.packaging-selector__total');
        if (!totalCell) return;

        totalCell.textContent = FoxTheme.Currency.formatMoney(this.getRowSubtotal(row), FoxTheme.settings.moneyFormat);
      }

      updateSectionTotal(packagingType) {
        const section = this.getSection(packagingType);
        if (!section) return;

        const totalAmount = section.querySelector('.packaging-selector__total-amount');
        let sectionTotal = 0;

        section.querySelectorAll('.packaging-selector__row').forEach((row) => {
          sectionTotal += this.getRowSubtotal(row);
        });

        totalAmount.textContent = FoxTheme.Currency.formatMoney(sectionTotal, FoxTheme.settings.moneyFormat) + ' USD';
      }

      handleClearAll(event) {
        const packagingType = event.currentTarget.dataset.packagingType;
        const section = this.getSection(packagingType);
        if (!section) return;

        section.querySelectorAll('.packaging-selector__input').forEach((input) => {
          input.value = 0;
          this.updateRowTotal(input.closest('.packaging-selector__row'));
        });

        this.updateSectionTotal(packagingType);
      }

      handleAddToCart(event) {
        event.preventDefault();
        const button = event.currentTarget;
        // Guard against double submits: a disabled/loading button can still receive a
        // programmatic or repeated activation, and each in-flight request adds the items again.
        if (button.disabled || button.classList.contains('btn--loading')) return;
        const packagingType = button.dataset.packagingType;
        const section = this.getSection(packagingType);
        if (!section) return;

        const items = [];
        let hasError = false;

        section.querySelectorAll('.packaging-selector__row').forEach((row) => {
          const input = row.querySelector('.packaging-selector__input');
          const quantity = parseInt(input.value) || 0;
          const maxQuantity = parseInt(row.dataset.maxQuantity);

          if (quantity <= 0) return;

          if (quantity > maxQuantity) {
            this.showErrorMessage(`Quantity for variant exceeds available amount (${maxQuantity}).`);
            hasError = true;
            return;
          }

          if (row.dataset.prePack === 'true') {
            const prePackScript = row.querySelector('.packaging-selector__pre-pack-items');
            let prePackItems = [];
            try {
              prePackItems = JSON.parse(prePackScript?.textContent || '[]');
            } catch (e) {
              console.error('Invalid pre-pack items JSON', e);
            }

            if (!prePackItems.length) {
              this.showErrorMessage('Pre-pack has no items configured.');
              hasError = true;
              return;
            }

            const rowVariantId = parseInt(row.dataset.variantId);
            const uniqueVariantIds = [...new Set(prePackItems.map((entry) => parseInt(entry.variant_id)))];
            const prePackProperties = {
              'Packaging Type': packagingType,
              '_Pre-Pack': 'Mixed Size Case',
              '_Pre-Pack Cases': String(quantity)
            };

            if (uniqueVariantIds.length === 1 && uniqueVariantIds[0] === rowVariantId) {
              // Single-variant pre-pack: add one case at a time.
              items.push({ id: rowVariantId, quantity: quantity, properties: prePackProperties });
            } else {
              // Multi-variant pre-pack: add each component.
              prePackItems.forEach((entry) => {
                items.push({
                  id: parseInt(entry.variant_id),
                  quantity: parseInt(entry.units) * quantity,
                  properties: prePackProperties
                });
              });
            }
          } else {
            items.push({
              id: parseInt(row.dataset.variantId),
              quantity: quantity,
              properties: { 'Packaging Type': packagingType }
            });
          }
        });

        if (hasError) return;

        if (items.length === 0) {
          this.showErrorMessage('Please select at least one item to add to cart.');
          return;
        }

        if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType !== 'drawer') {
          this.allowNavigation = true;
          // Full-page redirect path: show loading until the browser navigates away so the
          // click still gets immediate feedback and can't be fired twice.
          this.toggleButtonLoading(button, true);
          FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { items: items } });
          return;
        }

        this.showErrorMessage();
        this.toggleButtonLoading(button, true);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items: items,
          sections: sectionsToBundle,
          section_url: window.location.pathname
        });

        fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              this.showErrorMessage(parsedState.description);
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', { detail: { errorMessage: parsedState.description } })
              );
              return;
            }

            const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
            document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

            this.cartDrawerElement && this.cartDrawerElement.show(button);
            this.handleClearAll({ currentTarget: button });
          })
          .catch((e) => {
            console.error(e);
            const networkError = 'Something went wrong adding to cart. Please try again.';
            this.showErrorMessage(networkError);
            document.dispatchEvent(
              new CustomEvent('product-ajax:error', { detail: { errorMessage: networkError } })
            );
          })
          .finally(() => {
            this.toggleButtonLoading(button, false);
          });
      }

      showErrorMessage(errorMessage = false) {
        const errorElement = this.querySelector('.packaging-selector__error');
        if (!errorElement) {
          if (errorMessage) alert(errorMessage);
          return;
        }

        if (errorMessage) {
          const messageElement = errorElement.querySelector('.packaging-selector__error-message');
          if (messageElement) messageElement.textContent = errorMessage;
          errorElement.removeAttribute('hidden');
        } else {
          errorElement.setAttribute('hidden', '');
        }
      }

      toggleButtonLoading(button, isLoading) {
        button.classList.toggle('btn--loading', isLoading);
        button.toggleAttribute('disabled', isLoading);
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }
  );
}
